<script lang="ts" setup>
import type {Area} from "#magnolia-layer/types/magnolia-types";
import {PhArrowRight} from "@phosphor-icons/vue";
import TextField from "~/components/form/TextField.vue";
import {toTypedSchema} from "@vee-validate/zod";
import * as z from "zod";
import {useForm} from "vee-validate";
import {FetchError} from "ofetch";
import {joinURL} from "ufo";
import {promiseTimeout} from "@vueuse/core";
import {createErrorMap} from "#base-layer/utils/zod/zod-i18n-form-error-map";
import SelectField from "~/components/form/SelectField.vue";
import {object} from "zod";
import CheckboxField from "~/components/form/CheckboxField.vue";

const props = defineProps<{
  metadata: Area;
  title: string;
  abstract?: string;
  formId: string;
}>();

const {t} = useI18n();
const formMsg = ref<string | null>(null);
const formMsgTranslated = computed(() => {
  return formMsg.value != null ? t(formMsg.value) : "";
});
const form = ref<HTMLFormElement>();

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().trim().min(1, {message: "label.required"}),
    email: z
      .string()
      .trim()
      .min(1, {message: "label.required"})
      .email({message: "label.form.invalid.email"}),
    customFields: object({
      industry: z.string().trim().min(1, {message: "label.required"}),
      signup: z.boolean({required_error: "label.required"}).refine((value) => value, {
        message: "label.required",
      }),
    }),
  }),
  {
    errorMap: createErrorMap(t),
  },
);

const {values, handleSubmit, isSubmitting, errors, resetForm} = useForm({
  validationSchema,
  initialValues: {
    name: "",
    email: "",
    customFields: {
      industry: "",
      signup: false
    },
  },
});

const onSuccess = () => {
  resetForm();
  formMsg.value = "label.form.state.submitted";
};
const onError = () => {
  formMsg.value = "label.form.state.error";
};

const dataLayer: Record<string, any>[] = import.meta.client ? (window.dataLayer ??= []) : [];
const formName = "Newsletter Signup";
const runtimeConfig = useRuntimeConfig();

const onSubmit = handleSubmit(async (values) => {
  await promiseTimeout(500);

  const base = runtimeConfig.public.contact.api;
  const projectId = runtimeConfig.public.contact.somfyId;
  const api = joinURL(
    base,
    `/projects/${projectId}/contact-forms/${props.formId}/email`,
  );
  $fetch(api, {
    method: "POST",
    body: {
      ...values,
    },
  })
    .then(() => {
      dataLayer.push({
        event: "form_submit",
        form_name: formName,
      });
      onSuccess();
    })
    .catch((error: FetchError) => {
      dataLayer.push({
        event: "form_failure",
        form_name: formName,
        error_message: error.message,
      });
      //const msg = error.data?.error ?? t("label.form.state.error");
      onError();
    });
});
</script>

<template>
  <section
    class="newsletter-signup col-span-full py-12 px-8 bg-black text-white border-t-[1px] border-t-neutral-600">
    <div class="flex flex-col items-end gap-4 lg:grid lg:inner-content-grid">
      <div class="newsletter-signup__body col-span-6">
        <h2 class="text-3xl uppercase">{{ title }}</h2>
        <p v-if="abstract">{{ abstract }}</p>
      </div>
      <div class="newsletter-signup__form col-span-6 w-full">
        <form
          class="form flex flex-col lg:flex-row items-end gap-3 lg:gap-6"
          ref="form"
          @submit="onSubmit"
          v-if="formId">
          <div class="fields w-full flex flex-col gap-2">
            <TextField
              name="name"
              :as-required="true"
              :label-visible="false"
              label=""
              :placeholder="$t('label.fullName')" />
            <TextField
              name="email"
              :as-required="true"
              :label-visible="false"
              label=""
              :placeholder="$t('label.emailAddress')" />
            <SelectField
              name="customFields.industry"
              label=""
              :placeholder="$t('label.industry')"
              :as-required="true"
              :label-visible="false"
              :options="'industries'" />
            <CheckboxField  name="customFields.signup"
                            :label="$t('label.signup')"
                            :as-required="true" />
            <div class="text-sunrise-200">{{ formMsgTranslated }}</div>
          </div>
          <div class="submission">
            <button
              class="link-with-icon text-sunrise-100 p-3 transition-colors duration-300 link-underline"
              type="submit"
              :disabled="isSubmitting">
              <span>{{
                isSubmitting ? $t("label.submitting") : $t("label.subscribe")
              }}</span>
              <PhArrowRight />
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped></style>
